import StateObserver from 'src/StateObserver';
import i18n from 'src/lib/i18n/i18n';
import { openPopupPromise } from 'src/lib/popups/popupOpenClose';
import { captureGenericError } from 'src/lib/sentry';
import { Feature } from 'src/replicant/analytics';
import { hideLoading, showLoading } from 'src/state/ui';
import { devSettings } from '../../lib/settings';
import {
  preloadRewardedVideoAd,
  shouldIgnoreAdError,
  showPreloadedRewardedVideoAdAsync,
} from './ads.util';
import { retry } from 'src/lib/utils';

/**
 * @returns true if the user has watched an ad and should be rewarded
 */
export async function showRewardedAd() {
  if (devSettings.get('simulateAds')) {
    return openPopupPromise('popupAd', {} as never);
  }

  try {
    await retry(
      () =>
        showPreloadedRewardedVideoAdAsync({
          feature: Feature.AD._,
          subFeature: Feature.AD.REWARDED_VIDEO,
        }),
      {
        // Retry up to 5 times.
        retryCount: 5,
        // Wait 1 second between retries.
        linearBackoff: 1000,
        // Don't retry if the user closes the ad before it completes.
        retryOnlyIf: (e: any) =>
          e.code !== 'USER_INPUT' && e.code !== 'INVALID_OPERATION',
      },
    );
  } catch (e: any) {
    // If the user closes the ad before it completes, don't show error popup.
    if (e.code === 'USER_INPUT') return false;

    // Report error to Sentry if applicable.
    if (!shouldIgnoreAdError(e)) {
      captureGenericError('Error showing rewarded ad', e);
    }

    // Hide loading spinner if necessary. So user can interact with the popup.
    const isLoading = StateObserver.getState().ui.loading;
    if (isLoading) {
      StateObserver.dispatch(hideLoading());
    }

    // Show an error popup.
    await openPopupPromise('popupError', {
      title: i18n('error.ads.title'),
      message: i18n('error.ads.message'),
    });

    // Show loading again after closing the error popup.
    if (isLoading) {
      StateObserver.dispatch(showLoading());
    }

    // Ad failed to show, bail out.
    return false;
  }

  preloadRewardedVideoAd('post-ad-show');

  // Ad successfully shown.
  return true;
}
